import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { PointerEvent, RefObject } from "react";

interface DragState {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

export interface ModalPosition {
  x: number;
  y: number;
}

const DRAG_EXCLUDE_SELECTOR =
  "input, textarea, button, [role='tab'], [role='option'], a, [data-no-drag]";

/**
 * Drag-to-move behavior for floating modal surfaces (settings, search).
 * `position` is null until the first drag — callers center via CSS until
 * then, and switch to fixed positioning once the user grabs the handle.
 * Position resets when the modal closes.
 */
export function useDraggableModal({
  open,
  resetKey,
  containerRef,
  dragHandleSelector,
  onPositionCommit,
}: {
  open: boolean;
  resetKey?: string | null;
  containerRef: RefObject<HTMLDivElement | null>;
  dragHandleSelector?: string;
  onPositionCommit?: (position: ModalPosition) => void;
}) {
  const dragRef = useRef<DragState | null>(null);
  const positionRef = useRef<ModalPosition | null>(null);
  const [position, setPosition] = useState<ModalPosition | null>(null);

  useLayoutEffect(() => {
    dragRef.current = null;
    positionRef.current = null;
    setPosition(null);
  }, [resetKey]);

  useEffect(() => {
    if (open) return;
    positionRef.current = null;
    setPosition(null);
  }, [open]);

  const clampPosition = useCallback((x: number, y: number, drag: DragState) => {
    const margin = 12;
    const maxX = Math.max(margin, window.innerWidth - drag.width - margin);
    const maxY = Math.max(margin, window.innerHeight - drag.height - margin);
    return {
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    };
  }, []);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest(DRAG_EXCLUDE_SELECTOR)) return;
      if (dragHandleSelector && !target.closest(dragHandleSelector)) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const drag = {
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        width: rect.width,
        height: rect.height,
      };
      dragRef.current = drag;
      const nextPosition = clampPosition(rect.left, rect.top, drag);
      positionRef.current = nextPosition;
      setPosition(nextPosition);
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
    },
    [clampPosition, containerRef, dragHandleSelector],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const nextPosition = clampPosition(
        event.clientX - drag.offsetX,
        event.clientY - drag.offsetY,
        drag,
      );
      positionRef.current = nextPosition;
      setPosition(nextPosition);
    },
    [clampPosition],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (positionRef.current) onPositionCommit?.(positionRef.current);
    },
    [onPositionCommit],
  );

  return {
    position,
    dragHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
